// React imports
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoadingScreen from './components/common/LoadingScreen';
import {Toaster} from "sonner";

// Import the CSS file for transitions
import './App.css'; 

// SuperTokens imports
import SuperTokens, { SuperTokensWrapper } from "supertokens-auth-react";
import { SessionAuth } from "supertokens-auth-react/recipe/session";
import { SuperTokensConfig } from "./services/authentication/supertokens";

// Common pages/components
import Layout from './components/layout/Layout';
import LandingPage from './pages/common/LandingPage';
import Error from './pages/common/Error';

// Checkout pages/components
import CheckoutPage from './pages/checkout/CheckoutPage';
import CheckoutSuccess from './pages/checkout/CheckoutSuccess';
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
import ComputingOrientationCheckout from './pages/checkout/orientation/ComputingOrientationCheckout';
import NursingOrientationCheckout from './pages/checkout/orientation/NursingOrientationCheckout';
import HealthSciOrientationCheckout from './pages/checkout/orientation/HealthSciOrientationCheckout';
import CommerceOrientationCheckout from './pages/checkout/orientation/CommerceOrientationCheckout';
import AsteriskCheckout from './pages/checkout/orientation/AsteriskCheckout';
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

// Authentication pages/components
import AuthenticationChoice from './pages/authentication/AuthenticationChoice';
import CreateOrganization from './pages/authentication/CreateOrganization';
import AccountOnboarding from './pages/authentication/AccountOnboarding';
import OrganizationLogin from './pages/authentication/OrganizationLogin';
import OrganizationJoin from './pages/authentication/OrganizationJoin';
import {ForgotPassword} from "./pages/authentication/ForgotPassword";
import AlreadyLoggedIn from './components/authentication/AlreadyLoggedIn';

// Organization pages/components
import OrganizationDashboard from './pages/organization/OrganizationDashboard';
import OrganizationMembers from './pages/organization/OrganizationMembers';
import OrganizationTransactions from './pages/organization/OrganizationTransactions';
import OrganizationFinances from './pages/organization/OrganizationFinances';
import EmailDashboard from './pages/email/EmailDashboard';

// Event pages/components
import CreateEvent from './pages/event/CreateEvent';
import EventsOnDay from './pages/event/EventsOnDay';
import ViewEvent from './pages/event/ViewEvent';
import Attendees from './pages/event/Attendees';
import NotifyAttendees from './pages/event/NotifyAttendees';

// Ticket pages/components
import TicketValidation from './pages/ticket/TicketValidation';
import TodoList from './pages/ticket/TodoList';

// SuperTokens initialization
SuperTokens.init(SuperTokensConfig);

function App() {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Simulate a loading delay.
        setTimeout(() => {
            setLoading(false);
        }, 200); // Increased delay for smoother transitions
    }, []);
    
    return (
        <SuperTokensWrapper>
            <Helmet>
                <title>TriEvents</title>
            </Helmet>
            <LoadingScreen
                isLoading={loading}
            >
                <Router>
                    <Routes>
                        {/* Error route outside of Layout */}
                        <Route path="/error" element={<Error />} />

                        {/* Checkout Routes */}
                        <Route path="/checkout/:uuid" element={<CheckoutPage />} />
                        <Route path="/checkout/success" element={<CheckoutSuccess />} />

                        {/* ////////////////////////////////////////////////////////////////////////////////////////////////////////////// */}
                        {/* Orientation Checkout Routes */}
                        {/* <Route path="/orientation/computing/:uuid" element={<ComputingOrientationCheckout />} /> */}
                        <Route path="/orientation/computing" element={<ComputingOrientationCheckout />} />
                        {/* <Route path="/orientation/nursing/:uuid" element={<NursingOrientationCheckout />} /> */}
                        <Route path="/orientation/nursing" element={<NursingOrientationCheckout />} />
                        {/* <Route path="/orientation/healthsciences/:uuid" element={<HealthSciOrientationCheckout />} /> */}
                        <Route path="/orientation/healthsciences" element={<HealthSciOrientationCheckout />} />
                        {/* <Route path="/orientation/commerce/:uuid" element={<CommerceOrientationCheckout />} /> */}
                        <Route path="/orientation/commerce" element={<CommerceOrientationCheckout />} />
                        {/* <Route path="/asterisk/:uuid" element={<AsteriskCheckout />} /> */}
                        <Route path="/asterisk" element={<AsteriskCheckout />} />
                        {/* ////////////////////////////////////////////////////////////////////////////////////////////////////////////// */}

                        {/* Landing Page Route */}
                        <Route path="/" element={<LandingPage />} />

                        {/* Authentication Routes */}
                        <Route path="/auth" element={<AlreadyLoggedIn><AuthenticationChoice /></AlreadyLoggedIn>} />
                        <Route path="/organization/create" element={<AlreadyLoggedIn><CreateOrganization /></AlreadyLoggedIn>} />
                        <Route path="/organization/onboarding" element={<AccountOnboarding />} />
                        <Route path="/:orgId/login" element={<AlreadyLoggedIn><OrganizationLogin /></AlreadyLoggedIn>} />
                        <Route path="/:orgId/join" element={<AlreadyLoggedIn><OrganizationJoin /></AlreadyLoggedIn>} />
                        <Route path="/:orgId/auth/reset-password" element={<ForgotPassword />}/>
                        
                        {/* Ticket Routes */}
                        <Route path="/ticket/validate/:uuid" element={<TicketValidation />} />
                        <Route path="/ticket/todo-list/:uuid" element={<TodoList/>} />

                        {/* Routes where the user is logged in */}
                        <Route path="/:orgId/*" element={
                            // Ensures all logged in pages have the proper layout
                            <Layout>
                                <Routes>
                                    {/* Organization Routes */}
                                    <Route path="/dashboard" element={<SessionAuth><OrganizationDashboard /></SessionAuth>} />
                                    <Route path="/members" element={<SessionAuth><OrganizationMembers /></SessionAuth>} />
                                    <Route path="/email-dashboard" element={<SessionAuth><EmailDashboard /></SessionAuth>} />
                                    <Route path="/transactions" element={<SessionAuth><OrganizationTransactions /></SessionAuth>} />
                                    <Route path="/finances" element={<SessionAuth><OrganizationFinances /></SessionAuth>} />

                                    {/* Event Routes */}
                                    <Route path="/events/create" element={<SessionAuth><CreateEvent /></SessionAuth>} />
                                    <Route path="/events/:date" element={<SessionAuth><EventsOnDay /></SessionAuth>} />
                                    <Route path="/event/:uuid" element={<SessionAuth><ViewEvent /></SessionAuth>} />
                                    <Route path="/event/:uuid/notify-attendees" element={<SessionAuth><NotifyAttendees/></SessionAuth>}></Route>
                                    <Route path="/event/:uuid/attendees" element={<SessionAuth><Attendees /></SessionAuth>} />

                                    {/* Error route inside of Layout, redirects to Error route outside of Layout */}
                                    <Route path="*" element={<Navigate to="/error" replace />} />
                                </Routes>
                            </Layout>
                        } />
                    </Routes>
                    <Toaster/>
                </Router>
            </LoadingScreen>
        </SuperTokensWrapper>
    );
}

export default App;
